import { useCallback, useState } from 'react';
import { listEndpoints } from '../../api/client';
import { useAsync } from '../../api/hooks';
import { RegisterEndpointForm } from './RegisterEndpointForm';
import { SendEventForm } from './SendEventForm';

/**
 * Collapsed by default and parked above the feed, so the demo loop is
 * send → watch it land → open the timeline, with no terminal involved.
 */
export function DemoPanel({ onEventSent }: { onEventSent: () => void }) {
  const [open, setOpen] = useState(false);

  const fetcher = useCallback((signal: AbortSignal) => listEndpoints(signal), []);
  const { data, loading, error, refresh } = useAsync(fetcher);

  const endpoints = data ?? [];
  const summary = error
    ? 'endpoints unavailable'
    : loading
      ? 'loading endpoints…'
      : `${endpoints.length} endpoint${endpoints.length === 1 ? '' : 's'} registered`;

  return (
    <section className="panel demo">
      <button
        type="button"
        className="demo__toggle"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls="demo-body"
      >
        <span className="demo__toggle-icon" aria-hidden="true">
          {open ? '−' : '+'}
        </span>
        <span className="demo__toggle-text">Send a test event</span>
        <span className="demo__toggle-meta">{summary}</span>
      </button>

      {open ? (
        <div id="demo-body" className="demo__body">
          <div className="demo__section">
            <h3 className="demo__heading">Ingest an event</h3>
            <SendEventForm endpoints={endpoints} endpointsLoaded={!loading} onSent={onEventSent} />
          </div>

          <div className="demo__section">
            <h3 className="demo__heading">Register a subscriber endpoint</h3>
            <p className="demo__lede">
              Events fan out to every registered endpoint that subscribes to their type. With none
              registered, an event is ingested and then has nowhere to go.
            </p>
            <RegisterEndpointForm onCreated={refresh} />
          </div>
        </div>
      ) : null}
    </section>
  );
}
